import { useTranslation } from "react-i18next";
import { Bookmark, Link2 } from "lucide-react";
import type { Manual } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { CategoryIcon } from "@/components/icons";
import { cn } from "@/lib/utils";
import { ManualStateBadge } from "./ManualStateBadge.js";
import { ManualLockBadge } from "./ManualLockBadge.js";
import { ManualKebabMenu } from "./ManualKebabMenu.js";
import { ManualCardImage } from "./ManualCardImage.js";
import type { ManualTransitionKey } from "./manual-transitions.js";

/** The props shared by the grid card and the {@link ManualListRow} — both render the same manual with the same actions. */
export interface ManualCardProps {
  manual: Manual;
  isFavorite: boolean;
  onToggleFavorite: (id: number) => void;
  /** Shown under the Archived filter → star hidden; kebab offers Unarchive/Delete instead of the active actions. */
  archived: boolean;
  /** Best-effort client gate for the kebab actions (server is authoritative). */
  canManage: boolean;
  /** The caller has the facility `confirm` tier — reveals the publish/unpublish/clear-flag review transitions (panel). */
  canConfirm: boolean;
  /** Open the manual's details drawer (the TITLE is the accessible trigger — keeps the star/kebab as isolated controls). */
  onOpenDetails: (m: Manual) => void;
  onDuplicate: (m: Manual) => void;
  onMove: (m: Manual) => void;
  onArchive: (m: Manual) => void;
  onUnarchive: (m: Manual) => void;
  onDelete: (m: Manual) => void;
  /** Fire a review transition for this manual (ADR-018) — the view maps the key to a mutation (or the mark dialog). */
  /** Optional since ADR-018 addendum §3 — supplied only where no editor is reachable (classic nav). */
  onTransition?: (m: Manual, key: ManualTransitionKey) => void;
}

/**
 * One manual as a grid card (mockup #14): the in-facility **category** + an **AR** tag on top, the **name** (a button
 * that opens the details drawer), then a divider and a footer with the **lifecycle badge** (left) + the **favourite
 * star** and **kebab** (right). Only the AR tag shows here — the full type set (Sensor / Handspeed) + the description
 * live in {@link ManualDetailsDrawer}. On ARCHIVED cards the star is hidden (restore-only) and the kebab offers
 * Unarchive/Delete.
 */
export function ManualCard({
  manual,
  isFavorite,
  onToggleFavorite,
  archived,
  canManage,
  canConfirm,
  onOpenDetails,
  onDuplicate,
  onMove,
  onArchive,
  onUnarchive,
  onDelete,
  onTransition,
}: ManualCardProps) {
  const { t } = useTranslation();
  return (
    // `relative`: the name button's `after:` overlay spans the whole card, so a click anywhere opens the details drawer
    // (owner request); the footer's badges + star/kebab sit above it (`relative z-10`) and keep their own behaviour.
    <div className="border-border/60 bg-background group relative flex cursor-pointer flex-col gap-2 rounded-xl border p-3 shadow-xs transition-[box-shadow,transform,border-color] duration-150 ease-out hover:-translate-y-0.5 hover:border-ring/50 hover:shadow-md motion-reduce:transition-none motion-reduce:hover:translate-y-0 dark:border-border/50">
      {/* Cover: the manual's CATEGORY image from the private bucket (presigned), else a placeholder — the legacy tile
          image, sourced correctly (FS Phase 4). Bleeds to the top edges over the card's padding. */}
      <ManualCardImage src={manual.categoryImageUrl} className="-mx-3 -mt-3 mb-1 aspect-video rounded-b-none rounded-t-xl max-sm:aspect-[3/1]" />

      {/* Top: the in-facility category (left) + the AR tag (right). */}
      {/* One fixed height, badge or not: the titles below line up across the grid (they jumped by the badge). */}
      <div className="flex h-5 items-center justify-between gap-2">
        <span className="text-muted-foreground inline-flex min-w-0 items-center gap-1 text-xs">
          <CategoryIcon aria-hidden="true" className="size-3.5 shrink-0" />
          <span className="truncate">{manual.categoryName}</span>
        </span>
        <div className="flex shrink-0 items-center gap-1">
          {/* "Linked" (legacy Verknüpfung) — this manual is a linked copy of another (ADR-020). */}
          {manual.parentManualId != null ? (
            <span className="bg-muted text-muted-foreground inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[0.65rem] font-medium">
              <Link2 aria-hidden="true" className="size-3" /> {t("manualManagement.badge.linked")}
            </span>
          ) : null}
          {manual.isArManual ? (
            <span className="bg-muted text-muted-foreground shrink-0 rounded px-1.5 py-0.5 text-[0.65rem] font-medium">{t("manualManagement.badge.ar")}</span>
          ) : null}
        </div>
      </div>

      {/* Name — the button that opens the details drawer (the card isn't wrapped in one, so the star + kebab stay
          independent controls). No description here (it lives in the drawer). */}
      <div className="min-h-0 flex-1">
        <h3 className="text-[0.9375rem] leading-5 font-semibold">
          <button
            type="button"
            onClick={() => onOpenDetails(manual)}
            className="focus-visible:ring-ring/50 line-clamp-2 rounded-sm text-left outline-none after:absolute after:inset-0 after:rounded-xl after:content-[''] hover:underline focus-visible:ring-2"
          >
            {manual.name}
          </button>
        </h3>
      </div>

      {/* Footer (above a divider): lifecycle badge (left) · favourite + kebab (right). */}
      <div className="border-border/50 flex items-center justify-between gap-2 border-t pt-2">
        <div className="relative z-10 flex min-w-0 items-center gap-1.5">
          <ManualStateBadge state={manual.state} comment={manual.markedComment} />
          {manual.locked ? <ManualLockBadge /> : null}
        </div>
        <div className="relative z-10 flex shrink-0 items-center gap-0.5">
          {!archived ? (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t(isFavorite ? "manualManagement.card.unfavourite" : "manualManagement.card.favourite", { name: manual.name })}
              aria-pressed={isFavorite}
              onPress={() => onToggleFavorite(manual.id)}
            >
              <Bookmark className={cn("size-4", isFavorite ? "fill-attention text-attention" : "text-muted-foreground")} />
            </Button>
          ) : null}
          <ManualKebabMenu
            manual={manual}
            state={manual.state}
            archived={archived}
            canManage={canManage}
            canConfirm={canConfirm}
            onDuplicate={() => onDuplicate(manual)}
            onMove={() => onMove(manual)}
            onArchive={() => onArchive(manual)}
            onUnarchive={() => onUnarchive(manual)}
            onDelete={() => onDelete(manual)}
            onTransition={onTransition ? (key) => onTransition(manual, key) : undefined}
          />
        </div>
      </div>
    </div>
  );
}
